/** AppleCare Customer Self-Service Portal — PUBLIC, token-scoped, no internal
 *  navigation.  Premium SUNTRONIX-branded multi-step flow:
 *  Your details → Enterprise question → Device → Verification → Plans →
 *  Summary → Consent → Submit → Acknowledgement. */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck, Loader2, CheckCircle2, AlertTriangle, Smartphone,
  ArrowRight, ArrowLeft, Lock,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const pub = axios.create({ baseURL: `${BACKEND}/api/public/applecare` });
const pubUpsell = axios.create({ baseURL: `${BACKEND}/api/public/applecare-upsell` });

const money = (a, c = "INR") =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: c || "INR", maximumFractionDigits: 0 }).format(a || 0);

export default function AppleCarePublicPortal() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [gate, setGate] = useState({ loading: true, error: null });
  const [step, setStep] = useState(1);

  // form
  const [f, setF] = useState({
    contact_name: "", contact_email: "", contact_mobile: "", company_name: "",
    country: "IN", customer_type: "INDIVIDUAL", is_enterprise: false,
    agreement_number: "", serial: "", consent: false,
  });
  const [verify, setVerify] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [plansResp, setPlansResp] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState(null);
  const [responded, setResponded] = useState(null);
  const [declined, setDeclined] = useState(false);

  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const respond = async (action) => {
    setErr(null);
    try {
      const { data } = await pubUpsell.post(`/${token}/respond`, { action });
      setResponded(data.message || "Thank you");
      if (action === "NOT_INTERESTED") setDeclined(true);
    } catch (e) { setErr(e.response?.data?.detail || "Could not record your response"); }
  };

  useEffect(() => {
    pub.get(`/${token}`)
      .then(({ data }) => {
        setCtx(data);
        setF((s) => ({ ...s,
          contact_name: s.contact_name || data.prefill?.contact_name || "",
          contact_email: s.contact_email || data.prefill?.contact_email || "",
          company_name: s.company_name || data.prefill?.company_name || "",
          country: data.market || "IN" }));
        setGate({ loading: false, error: null });
      })
      .catch((e) => setGate({ loading: false, error: e.response?.data?.detail || "This link is not available." }));
  }, [token]);

  const doVerify = async () => {
    setErr(null); setVerifying(true); setPlansResp(null); setSelected(null);
    try {
      const { data } = await pub.post(`/${token}/verify`, { serial: f.serial });
      setVerify(data);
      setStep(4);
    } catch (e) { setErr(e.response?.data?.detail || "Verification failed"); }
    finally { setVerifying(false); }
  };

  const loadPlans = async () => {
    setErr(null); setLoadingPlans(true);
    try {
      const { data } = await pub.post(`/${token}/eligibility`, {
        customer_type: f.customer_type, company_name: f.company_name,
        is_enterprise: f.is_enterprise, agreement_number: f.agreement_number, serial: f.serial,
      });
      setPlansResp(data);
      setStep(5);
    } catch (e) { setErr(e.response?.data?.detail || "Could not load plans"); }
    finally { setLoadingPlans(false); }
  };

  const submit = async () => {
    setErr(null); setSubmitting(true);
    try {
      const { data } = await pub.post(`/${token}/submit`, {
        ...f, selected_plan_id: selected?.id || "none",
      });
      setDone(data);
      setStep(8);
    } catch (e) { setErr(e.response?.data?.detail || "Submission failed"); }
    finally { setSubmitting(false); }
  };

  if (gate.loading) return <Shell><div className="py-24 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-white/70" /></div></Shell>;
  if (gate.error) return <Shell><div className="py-20 text-center" data-testid="applecare-link-error">
    <AlertTriangle className="h-8 w-8 mx-auto text-amber-400 mb-3" />
    <div className="text-lg font-semibold">This link is not available</div>
    <div className="text-white/60 text-sm mt-1">{gate.error}</div>
  </div></Shell>;

  const canDetails = f.contact_name && f.contact_email.includes("@") && f.contact_mobile &&
    (f.customer_type === "INDIVIDUAL" || f.company_name);
  const canDevice = f.serial.trim().length >= 4;
  const familyKnown = verify?.family;

  return (
    <Shell acr={ctx?.acr_id} brand={ctx?.brand_name}>
      <div className="max-w-xl mx-auto" data-testid="applecare-portal">
        <Stepper step={step} />
        {err && <div className="mb-4 rounded-lg bg-red-500/15 border border-red-400/30 text-red-200 text-sm px-4 py-2" data-testid="applecare-error">{err}</div>}

        {ctx?.device_context?.family_label && step < 8 && (
          <div className="mb-4 rounded-xl border border-emerald-400/25 bg-emerald-500/10 p-3" data-testid="ac-device-banner">
            <div className="text-[11px] uppercase tracking-wide text-emerald-300/80">Your recent purchase from SUNTRONIX</div>
            <div className="text-sm font-semibold mt-0.5" style={{ color: "#fff" }}>{ctx.device_context.product_name || ctx.device_context.family_label}</div>
            {ctx.device_context.invoice_number && <div className="text-[11px] text-white/50">Invoice {ctx.device_context.invoice_number}</div>}
            {ctx.upsell_id && !responded && (
              <div className="flex flex-wrap gap-2 mt-2" data-testid="ac-response-bar">
                <button onClick={() => respond("INTERESTED")} data-testid="ac-resp-interested" className="text-xs h-8 px-3 rounded-full bg-white text-slate-900 font-semibold">I'm interested</button>
                <button onClick={() => respond("CALLBACK")} data-testid="ac-resp-callback" className="text-xs h-8 px-3 rounded-full border border-white/30 text-white/80">Request a call back</button>
                <button onClick={() => respond("NOT_INTERESTED")} data-testid="ac-resp-decline" className="text-xs h-8 px-3 rounded-full border border-white/30 text-white/75">Not interested</button>
              </div>
            )}
            {responded && <div className="text-xs text-emerald-300 mt-2" data-testid="ac-responded">{responded}</div>}
          </div>
        )}

        {declined && (
          <Card title="Thank you">
            <div className="text-center py-4" data-testid="ac-declined-card">
              <div className="text-sm text-white/70">You've let us know AppleCare isn't needed for this device. We won't send any further reminders. If you change your mind, just reopen this link.</div>
            </div>
          </Card>
        )}

        {/* STEP 1 — your details */}
        {step === 1 && !declined && (
          <Card title="Your details">
            <Field label="Contact name *"><Input v={f.contact_name} on={(v) => upd("contact_name", v)} testId="ac-contact-name" /></Field>
            <Field label="Contact number *"><Input v={f.contact_mobile} on={(v) => upd("contact_mobile", v)} testId="ac-contact-mobile" /></Field>
            <Field label="Email address *"><Input v={f.contact_email} on={(v) => upd("contact_email", v)} testId="ac-contact-email" /></Field>
            <Field label="Customer type *">
              <div className="grid grid-cols-3 gap-2">
                {["INDIVIDUAL", "BUSINESS", "ENTERPRISE"].map((t) => (
                  <button key={t} data-testid={`ac-ctype-${t}`}
                    onClick={() => setF((s) => ({ ...s, customer_type: t,
                      company_name: t === "INDIVIDUAL" ? "" : s.company_name }))}
                    className={`h-10 rounded-lg text-xs font-semibold border transition-colors ${f.customer_type === t ? "bg-white text-slate-900 border-white" : "border-white/20 text-white/70 hover:border-white/50"}`}>
                    {t === "INDIVIDUAL" ? "Personal" : t.charAt(0) + t.slice(1).toLowerCase()}
                  </button>
                ))}
              </div>
            </Field>
            {(f.customer_type !== "INDIVIDUAL") &&
              <Field label="Company name *"><Input v={f.company_name} on={(v) => upd("company_name", v)} testId="ac-company" /></Field>}
            <Next disabled={!canDetails} onClick={() => setStep(2)} />
          </Card>
        )}

        {/* STEP 2 — enterprise question */}
        {step === 2 && !declined && (
          <Card title="AppleCare for Enterprise">
            <p className="text-sm text-white/70 mb-3">Are you purchasing under an AppleCare for Enterprise Agreement?</p>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <button data-testid="ac-enterprise-no" onClick={() => { upd("is_enterprise", false); }}
                className={`h-11 rounded-lg text-sm font-semibold border ${!f.is_enterprise ? "bg-white text-slate-900 border-white" : "border-white/20 text-white/70"}`}>No</button>
              <button data-testid="ac-enterprise-yes" onClick={() => upd("is_enterprise", true)}
                className={`h-11 rounded-lg text-sm font-semibold border ${f.is_enterprise ? "bg-white text-slate-900 border-white" : "border-white/20 text-white/70"}`}>Yes</button>
            </div>
            {f.is_enterprise && (
              <>
                <Field label="Apple Agreement Number *"><Input v={f.agreement_number} on={(v) => upd("agreement_number", v)} testId="ac-agreement-number" /></Field>
                {!f.company_name && <Field label="Company name *"><Input v={f.company_name} on={(v) => upd("company_name", v)} testId="ac-company-2" /></Field>}
                <p className="text-[11px] text-white/40 flex items-center gap-1"><Lock className="h-3 w-3" /> Verified securely against SUNTRONIX records. Your number is never shown to others.</p>
              </>
            )}
            <Row><Back onClick={() => setStep(1)} /><Next disabled={f.is_enterprise && (!f.agreement_number || !f.company_name)} onClick={() => setStep(3)} /></Row>
          </Card>
        )}

        {/* STEP 3 — device */}
        {step === 3 && !declined && (
          <Card title="Your Apple device">
            {ctx?.device_context?.family ? (
              <>
                <p className="text-sm text-white/70 mb-3">We already have your device on file from your SUNTRONIX purchase.</p>
                <div className="rounded-lg bg-white/5 border border-white/15 p-3 mb-3">
                  <div className="text-[11px] uppercase tracking-wide text-white/50">Your device</div>
                  <div className="text-base font-semibold" style={{ color: "#fff" }}>{ctx.device_context.product_name || ctx.device_context.family_label}</div>
                </div>
                <button disabled={loadingPlans} onClick={loadPlans} data-testid="ac-view-plans-known"
                  className="w-full h-11 rounded-lg bg-white text-slate-900 font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40">
                  {loadingPlans ? <Loader2 className="h-4 w-4 animate-spin" /> : null} View AppleCare options for this device <ArrowRight className="h-4 w-4" />
                </button>
                <div className="mt-3 pt-3 border-t border-white/10">
                  <label className="block text-[11px] uppercase tracking-wide text-white/50 mb-1">Different device? Enter its Serial / IMEI</label>
                  <Input v={f.serial} on={(v) => upd("serial", v)} testId="ac-serial" mono />
                  <button disabled={!canDevice || verifying} onClick={doVerify} data-testid="ac-check-device"
                    className="mt-2 h-9 px-4 rounded-lg border border-white/20 text-white/80 text-sm inline-flex items-center gap-2 disabled:opacity-30">
                    {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Smartphone className="h-4 w-4" />} Check this device
                  </button>
                </div>
                <div className="mt-4"><Back onClick={() => setStep(2)} /></div>
              </>
            ) : (
              <>
                <p className="text-sm text-white/70 mb-3">Enter your device Serial Number or IMEI.</p>
                <Field label="Serial Number / IMEI *"><Input v={f.serial} on={(v) => upd("serial", v)} testId="ac-serial" mono /></Field>
                <Row><Back onClick={() => setStep(2)} />
                  <button disabled={!canDevice || verifying} onClick={doVerify} data-testid="ac-check-device"
                    className="ml-auto h-11 px-5 rounded-lg bg-white text-slate-900 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-40">
                    {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Smartphone className="h-4 w-4" />} Check device
                  </button>
                </Row>
              </>
            )}
          </Card>
        )}

        {/* STEP 4 — verification result */}
        {step === 4 && verify && (
          <Card title="Device verification">
            {familyKnown ? (
              <div className="rounded-lg bg-emerald-500/15 border border-emerald-400/30 p-4 mb-3" data-testid="ac-verify-known">
                <div className="flex items-center gap-2 text-emerald-300 text-xs font-semibold uppercase tracking-wide mb-2"><CheckCircle2 className="h-4 w-4" /> {verify.status.replace(/_/g, " ")}</div>
                <div className="text-lg font-semibold">{verify.model || verify.family_label}</div>
                <div className="text-sm text-white/60">Family: {verify.family_label} · Serial {verify.serial_masked} · Source {verify.source}</div>
              </div>
            ) : (
              <div className="rounded-lg bg-amber-500/15 border border-amber-400/30 p-4 mb-3" data-testid="ac-verify-review">
                <div className="flex items-center gap-2 text-amber-300 text-xs font-semibold uppercase tracking-wide mb-2"><AlertTriangle className="h-4 w-4" /> We need to verify your device</div>
                <div className="text-sm text-white/70">We couldn't automatically identify this device ({verify.serial_masked}). You can submit your request and SUNTRONIX will verify it, or verify with Apple.</div>
                <a href="https://checkcoverage.apple.com" target="_blank" rel="noreferrer" data-testid="ac-verify-apple"
                  className="inline-flex items-center gap-1 mt-2 text-xs text-white underline">Verify with Apple <ArrowRight className="h-3 w-3" /></a>
                <div className="text-[10px] text-white/30 mt-1">Apple Check Coverage is operated by Apple. SUNTRONIX is not Apple's Check Coverage service.</div>
              </div>
            )}
            <Row><Back onClick={() => setStep(3)} />
              {familyKnown ? (
                <button onClick={loadPlans} disabled={loadingPlans} data-testid="ac-view-plans"
                  className="ml-auto h-11 px-5 rounded-lg bg-white text-slate-900 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-40">
                  {loadingPlans ? <Loader2 className="h-4 w-4 animate-spin" /> : null} View AppleCare options <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button onClick={() => setStep(6)} data-testid="ac-submit-review"
                  className="ml-auto h-11 px-5 rounded-lg bg-white text-slate-900 font-semibold text-sm">Submit for review</button>
              )}
            </Row>
          </Card>
        )}

        {/* STEP 5 — plans */}
        {step === 5 && plansResp && !declined && (
          <Card title={`AppleCare options for ${plansResp.device_family_label || "your device"}`}>
            {plansResp.plans.length === 0 ? (
              <div className="text-sm text-white/70" data-testid="ac-no-plans">
                {plansResp.message || "No applicable plans were found. SUNTRONIX will review your request."}
              </div>
            ) : (
              <div className="space-y-2" data-testid="ac-plan-list">
                {plansResp.plans.map((p) => (
                  <button key={p.id} onClick={() => setSelected(p)} data-testid={`ac-plan-${p.id}`}
                    className={`w-full text-left rounded-xl border p-4 transition-colors ${selected?.id === p.id ? "border-white bg-white/10" : "border-white/15 hover:border-white/40"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold">{p.plan_name}</div>
                        <div className="text-[11px] text-white/50 font-mono">Part #{p.apple_part_number}</div>
                        <div className="text-xs text-white/60 mt-1">{p.coverage_type}{p.duration_months ? ` · ${p.duration_months} months` : ""}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-lg font-bold">{money(p.amount, p.currency)}</div>
                        <div className="text-[11px] text-white/50">{p.payment_frequency_label}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            <Row><Back onClick={() => setStep(4)} />
              <button disabled={!selected} onClick={() => setStep(6)} data-testid="ac-select-plan"
                className="ml-auto h-11 px-5 rounded-lg bg-white text-slate-900 font-semibold text-sm disabled:opacity-40">Continue</button>
            </Row>
          </Card>
        )}

        {/* STEP 6 — summary + consent */}
        {step === 6 && !declined && (
          <Card title="Review & submit">
            <dl className="text-sm divide-y divide-white/10" data-testid="ac-summary">
              <SumRow k="Contact" v={f.contact_name} />
              <SumRow k="Email" v={f.contact_email} />
              {f.company_name && <SumRow k="Company" v={f.company_name} />}
              {f.is_enterprise && <SumRow k="Agreement" v={"••••••" + (f.agreement_number.slice(-4) || "")} />}
              <SumRow k="Device" v={verify?.model || verify?.family_label || "Verification required"} />
              {selected && <SumRow k="Plan" v={`${selected.plan_name} — ${money(selected.amount, selected.currency)} · ${selected.payment_frequency_label}`} />}
            </dl>
            <div className="text-[11px] text-white/50 mt-3">Final AppleCare activation is subject to successful device / plan eligibility verification and applicable Apple terms.</div>
            <label className="flex items-start gap-2 mt-3 text-xs text-white/70 cursor-pointer">
              <input type="checkbox" checked={f.consent} onChange={(e) => upd("consent", e.target.checked)} data-testid="ac-consent" className="mt-0.5" />
              I consent to SUNTRONIX using my contact and device details to process this AppleCare request, per the applicable Terms & Privacy policy.
            </label>
            <Row><Back onClick={() => setStep(selected ? 5 : 4)} />
              <button disabled={!f.consent || submitting} onClick={submit} data-testid="ac-submit"
                className="ml-auto h-11 px-6 rounded-lg bg-white text-slate-900 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-40">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Confirm & submit
              </button>
            </Row>
          </Card>
        )}

        {/* STEP 8 — acknowledgement */}
        {step === 8 && done && (
          <Card title="Request received">
            <div className="text-center py-4" data-testid="ac-ack">
              <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-400 mb-3" />
              <div className="text-lg font-semibold">Thank you, {f.contact_name.split(" ")[0]}</div>
              <div className="text-sm text-white/70 mt-1">Your AppleCare request <span className="font-mono">{done.acr_id}</span> has been received.</div>
              <div className="text-xs text-white/50 mt-3">{done.message}</div>
            </div>
          </Card>
        )}
      </div>
    </Shell>
  );
}

/* ---------- presentational ---------- */
function Shell({ children, acr, brand }) {
  return (
    <div className="min-h-screen bg-[#0A0E1A] text-white">
      <div className="border-b border-white/10">
        <div className="max-w-xl mx-auto px-4 py-5 flex items-center gap-3">
          <ShieldCheck className="h-6 w-6 text-emerald-400" />
          <div>
            <div className="font-semibold tracking-tight">{brand || "SUNTRONIX"}</div>
            {acr && <div className="text-[11px] text-white/40 font-mono">{acr}</div>}
          </div>
        </div>
      </div>
      <div className="max-w-xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-1" style={{ color: "#fff" }}>Protect your Apple device</h1>
        <p className="text-sm text-white/60 mb-6">Enter your device details and we'll help identify the applicable AppleCare options.</p>
        {children}
      </div>
    </div>
  );
}
const STEPS = ["You", "Enterprise", "Device", "Verify", "Plans", "Confirm"];
function Stepper({ step }) {
  return (
    <div className="flex items-center gap-1 mb-5">
      {STEPS.map((s, i) => (
        <div key={s} className={`h-1 flex-1 rounded-full ${i < step ? "bg-emerald-400" : "bg-white/15"}`} />
      ))}
    </div>
  );
}
const Card = ({ title, children }) => (
  <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5">
    <h2 className="text-base font-semibold mb-4" style={{ color: "#fff" }}>{title}</h2>
    {children}
  </div>
);
const Field = ({ label, children }) => (
  <div className="mb-3"><label className="block text-[11px] uppercase tracking-wide text-white/60 mb-1">{label}</label>{children}</div>
);
const Input = ({ v, on, testId, mono }) => (
  <input value={v} onChange={(e) => on(e.target.value)} data-testid={testId}
    className={`w-full h-11 rounded-lg bg-white/5 border border-white/15 px-3 text-sm focus:border-white/50 outline-none ${mono ? "font-mono tracking-wider uppercase" : ""}`} />
);
const Row = ({ children }) => <div className="flex items-center gap-2 mt-4">{children}</div>;
const Back = ({ onClick }) => (
  <button onClick={onClick} data-testid="ac-back" className="h-11 px-4 rounded-lg border border-white/20 text-white/70 text-sm inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Back</button>
);
const Next = ({ onClick, disabled }) => (
  <button onClick={onClick} disabled={disabled} data-testid="ac-next" className="ml-auto h-11 px-5 rounded-lg bg-white text-slate-900 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-40">Continue <ArrowRight className="h-4 w-4" /></button>
);
const SumRow = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-2"><dt className="text-white/50">{k}</dt><dd className="text-right font-medium">{v}</dd></div>
);
